import React from "react";
import { connect } from "react-redux";
import Traec from "traec";
import DataTable from "react-data-table-component";
import { BSCard, BSBtn, BSBtnDropdown } from "traec-react/utils/bootstrap";
import { ProjectAuthGroupForm } from "./projectForm";
import CompanyAuthGroupForm from "./companyForm";
import AuthGroupItem from "./item";
import Octicon from "react-octicon";

function PermissionListHeader({ projectId, permissionCount }) {
  return (
    <div className="card shadow-sm mb-4">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div className="d-flex align-items-center mb-2">
              <h1 className="h3 mb-0 text-primary">User Permissions</h1>
            </div>
            <p className="text-muted mb-2">
              {projectId ? "Project permission management" : "Organisation permission management"}
            </p>
            <p className="text-muted small mb-0">
              <strong>Total permission groups:</strong> {permissionCount}
            </p>
          </div>
          <div className="text-end">
            <span className="badge badge-primary">Permission Management</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function PermissionDescription({ projectId }) {
  return (
    <div className="mb-4">
      <h2 className="h4 text-dark mb-2">Permission Groups</h2>
      <p className="text-muted">
        {projectId
          ? "Create and manage permission groups for project access. These groups define what actions users can perform within the project."
          : "Create and manage permission groups for organisation access. These groups define what actions users can perform across different areas of your organisation."}
      </p>
      <div className="alert alert-info">
        <div className="d-flex">
          <Octicon name="shield" className="me-2 mt-1" />
          <div>
            <strong>Permission Management:</strong> Create custom permission groups to control user access levels. Each
            group can have specific permissions assigned, and users can be added to these groups to inherit the
            permissions. Groups marked as "In Use" cannot be deleted as they have active users assigned.
          </div>
        </div>
      </div>
    </div>
  );
}

export class AuthGroupList extends React.Component {
  constructor(props) {
    super(props);

    if (props.projectId) {
      const projectId = props.projectId;
      this.fetch = new Traec.Fetch("project_authgroup", "post", { projectId });
      this.requiredFetches = [
        new Traec.Fetch("project_authgroup", "list"),
        new Traec.Fetch("project_discipline", "list"),
      ];
    } else if (props.companyId) {
      const companyId = props.companyId;
      this.fetch = new Traec.Fetch("company_authgroup", "post", { companyId });
      this.requiredFetches = [new Traec.Fetch("company_authgroup", "list")];
    }

    let { fetchParams, stateParams } = this.fetch.params;

    this.state = {
      showForm: false,
      editingItem: null,
      formParams: {
        fetchParams,
        stateParams,
        initFields: {},
      },
    };

    this.onClick = this.onClick.bind(this);
    this.showFormHandler = this.showFormHandler.bind(this);
    this.deleteAuthGroup = this.deleteAuthGroup.bind(this);
    this.editAuthGroup = this.editAuthGroup.bind(this);
    this.getColumns = this.getColumns.bind(this);
  }

  componentDidMount() {
    Traec.fetchRequired.bind(this)();
  }

  componentDidUpdate() {
    Traec.fetchRequired.bind(this)();
  }

  onClick(e) {
    e.preventDefault();
    this.fetch.toggleForm();
  }

  showFormHandler(e, showForm) {
    this.setState({ showForm });
  }

  deleteAuthGroup(item) {
    let { projectId, companyId } = this.props;
    let authGroupId = item.get("uid");
    let fetch = null;
    if (projectId) {
      fetch = new Traec.Fetch("project_authgroup", "delete", { projectId, authGroupId });
    } else if (companyId) {
      fetch = new Traec.Fetch("company_authgroup", "delete", { companyId, authGroupId });
    }
    fetch.updateFetchParams({
      postSuccessHook: () => {
        location.reload();
      },
    });
    fetch.dispatch();
  }

  editAuthGroup(item) {
    // Toggle edit form for the specific item
    const currentlyEditing = this.state.editingItem && this.state.editingItem.get("uid") === item.get("uid");
    this.setState({
      editingItem: currentlyEditing ? null : item,
    });
  }

  isInUse(item) {
    let { disciplines } = this.props;
    if (!disciplines) {
      return true;
    }
    return disciplines.toList().filter((d) => d.getInPath("auth.uid") == item.get("uid")).size > 0;
  }

  getColumns() {
    const columns = [
      {
        name: "Name",
        selector: (row) => row.get("name"),
        sortable: true,
        grow: 2,
        wrap: true,
        cell: (row) => (
          <div className="d-flex flex-column">
            <span className="fw-medium">{row.get("name")}</span>
            {row.get("description") && <small className="text-muted">{row.get("description")}</small>}
          </div>
        ),
      },
      {
        name: "Status",
        cell: (row) => {
          const inUse = this.isInUse(row);
          return !inUse ? (
            <span className="badge bg-warning text-dark">Available</span>
          ) : (
            <span className="badge bg-success">In Use</span>
          );
        },
        sortable: true,
        selector: (row) => (this.isInUse(row) ? "In Use" : "Available"),
        center: true,
        width: "120px",
      },
      {
        name: "Actions",
        cell: (row) => {
          const inUse = this.isInUse(row);
          return (
            <div className="d-flex gap-1">
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => this.editAuthGroup(row)}
                title="Edit Permission Group"
              >
                <Octicon name="pencil" />
              </button>
              {!inUse && (
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => this.deleteAuthGroup(row)}
                  title="Delete Permission Group"
                >
                  <Octicon name="trash" />
                </button>
              )}
            </div>
          );
        },
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
        width: "100px",
      },
    ];

    return columns;
  }

  /* RENDERING */

  renderForm() {
    if (!this.state.showForm) {
      return null;
    }

    if (this.props.projectId) {
      return (
        <ProjectAuthGroupForm
          item={null}
          projectId={this.props.projectId}
          showFormHandler={this.showFormHandler}
          fetchHandler={this.fetch}
          dispatch={this.props.dispatch}
        />
      );
    }

    if (this.props.companyId) {
      return (
        <CompanyAuthGroupForm
          item={null}
          companyId={this.props.companyId}
          showFormHandler={this.showFormHandler}
          fetchHandler={this.fetch}
          dispatch={this.props.dispatch}
        />
      );
    }
  }

  renderEditForm() {
    if (!this.state.editingItem) {
      return null;
    }

    const item = this.state.editingItem;
    const authGroupId = item.get("uid");

    // Create edit fetch handler for this specific item
    let editFetch = null;
    if (this.props.projectId) {
      editFetch = new Traec.Fetch("project_authgroup", "put", {
        projectId: this.props.projectId,
        authGroupId,
      });
    } else if (this.props.companyId) {
      editFetch = new Traec.Fetch("company_authgroup", "put", {
        companyId: this.props.companyId,
        authGroupId,
      });
    }

    const closeEditForm = () => {
      this.setState({ editingItem: null });
    };

    if (this.props.projectId) {
      return (
        <div className="row mt-3">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-header">
                <h5>Edit Role: {item.get("name")}</h5>
              </div>
              <div className="card-body">
                <ProjectAuthGroupForm
                  item={item}
                  projectId={this.props.projectId}
                  showFormHandler={(e, show) => !show && closeEditForm()}
                  fetchHandler={editFetch}
                  dispatch={this.props.dispatch}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (this.props.companyId) {
      return (
        <div className="row mt-3">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-header">
                <h5>Edit Role: {item.get("name")}</h5>
              </div>
              <div className="card-body">
                <CompanyAuthGroupForm
                  item={item}
                  companyId={this.props.companyId}
                  showFormHandler={(e, show) => !show && closeEditForm()}
                  fetchHandler={editFetch}
                  dispatch={this.props.dispatch}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  }

  renderEditFormContent() {
    if (!this.state.editingItem) {
      return null;
    }

    const item = this.state.editingItem;
    const authGroupId = item.get("uid");

    // Create edit fetch handler for this specific item
    let editFetch = null;
    if (this.props.projectId) {
      editFetch = new Traec.Fetch("project_authgroup", "put", {
        projectId: this.props.projectId,
        authGroupId,
      });
    } else if (this.props.companyId) {
      editFetch = new Traec.Fetch("company_authgroup", "put", {
        companyId: this.props.companyId,
        authGroupId,
      });
    }

    const closeEditForm = () => {
      this.setState({ editingItem: null });
    };

    if (this.props.projectId) {
      return (
        <ProjectAuthGroupForm
          item={item}
          projectId={this.props.projectId}
          showFormHandler={(e, show) => !show && closeEditForm()}
          fetchHandler={editFetch}
          dispatch={this.props.dispatch}
        />
      );
    }

    if (this.props.companyId) {
      return (
        <CompanyAuthGroupForm
          item={item}
          companyId={this.props.companyId}
          showFormHandler={(e, show) => !show && closeEditForm()}
          fetchHandler={editFetch}
          dispatch={this.props.dispatch}
        />
      );
    }

    return null;
  }

  render() {
    let { authGroups } = this.props;
    if (!authGroups) {
      return null;
    }

    let authGroupsList = authGroups.toList();
    const { editingItem, showForm } = this.state;
    const { projectId } = this.props;

    if (editingItem) {
      return (
        <div className="container-fluid py-4">
          <div className="row">
            <div className="col-12">
              <div className="card shadow-sm mb-4">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="d-flex align-items-center mb-2">
                        <button
                          onClick={() => this.setState({ editingItem: null })}
                          className="btn btn-sm btn-outline-secondary me-3 mr-3"
                          title="Back to Permission List"
                        >
                          <Octicon name="chevron-left" /> Back to User Permissions
                        </button>
                        <h1 className="h3 mb-0 text-primary">Edit Permission Group</h1>
                      </div>
                      <p className="text-muted mb-0">{editingItem.get("name")}</p>
                    </div>
                    <div className="text-end">
                      <span className="badge badge-primary">Editing</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card shadow-sm">
                <div className="card-body">{this.renderEditFormContent()}</div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="container-fluid py-4">
        <div className="row">
          <div className="col-12">
            <PermissionListHeader projectId={projectId} permissionCount={authGroupsList.size} />

            <div className="card shadow-sm">
              <div className="card-body">
                <PermissionDescription projectId={projectId} />

                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h5 className="mb-0">Permission Groups</h5>
                  </div>
                  <button
                    className="btn btn-sm btn-primary d-flex align-items-center gap-2"
                    onClick={(e) => this.showFormHandler(e, !showForm)}
                  >
                    <Octicon name="plus" />
                    Add Permission Group
                  </button>
                </div>

                {showForm && (
                  <div className="mb-4">
                    <div className="card border-primary">
                      <div className="card-header bg-primary text-white">
                        <h6 className="mb-0">Create New Permission Group</h6>
                      </div>
                      <div className="card-body">{this.renderForm()}</div>
                    </div>
                  </div>
                )}

                <div className="table-responsive">
                  <DataTable
                    columns={this.getColumns()}
                    data={authGroupsList.toArray()}
                    highlightOnHover={true}
                    pagination={authGroupsList.size > 10}
                    paginationPerPage={10}
                    noDataComponent={
                      <div className="text-center py-5">
                        <Octicon name="shield" className="text-muted mb-3" style={{ fontSize: "48px" }} />
                        <h5 className="text-muted">No permission groups found</h5>
                        <p className="text-muted">
                          Create permission groups to control user access levels within your{" "}
                          {projectId ? "project" : "organisation"}.
                        </p>
                        <button className="btn btn-outline-primary" onClick={(e) => this.showFormHandler(e, true)}>
                          <Octicon name="plus" className="me-2" />
                          Create First Permission Group
                        </button>
                      </div>
                    }
                    customStyles={{
                      table: {
                        style: {
                          borderRadius: "8px",
                        },
                      },
                      headRow: {
                        style: {
                          backgroundColor: "#f8f9fa",
                          borderTopLeftRadius: "8px",
                          borderTopRightRadius: "8px",
                        },
                      },
                      rows: {
                        style: {
                          "&:hover": {
                            backgroundColor: "#f8f9fa",
                          },
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  let { projectId, companyId } = ownProps;

  let project = null;
  let company = null;
  let authGroups = null;
  let disciplines = null;

  if (projectId) {
    project = state.getInPath(`entities.projects.byId.${projectId}`);
    authGroups = state.getInPath(`entities.projectObjects.byId.${projectId}.authGroups`);
    disciplines = state.getInPath(`entities.projectObjects.byId.${projectId}.disciplines`);
  }

  if (companyId) {
    company = state.getInPath(`entities.companies.byId.${companyId}`);
    authGroups = state.getInPath(`entities.companyObjects.byId.${companyId}.authGroups`);
  }
  let isAuthenticated = state.getInPath("auth.isAuthenticated");
  return { project, company, authGroups, disciplines, isAuthenticated };
};

export default connect(mapStateToProps)(AuthGroupList);
